import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { getTimezoneOptions } from '@/lib/date-utils';
import { useTimezone } from '@/contexts/TimezoneContext';
import { toast } from 'sonner';
import { Globe, Clock, Check } from 'lucide-react';

export default function UserTimezoneSettings() {
  const { userTimezone, setUserTimezone, loading } = useTimezone();
  const [selectedTimezone, setSelectedTimezone] = useState<string>(userTimezone);
  const [isSaving, setIsSaving] = useState(false);
  const timezoneOptions = getTimezoneOptions();

  useEffect(() => {
    if (userTimezone) {
      setSelectedTimezone(userTimezone);
    }
  }, [userTimezone]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await setUserTimezone(selectedTimezone);
      toast.success('Configurações de fuso horário atualizadas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações de fuso horário:', error);
      toast.error('Não foi possível salvar as configurações de fuso horário.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" /> Configurações de Fuso Horário
          </CardTitle>
          <CardDescription>
            Carregando configurações de fuso horário...
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" /> Configurações de Fuso Horário
        </CardTitle>
        <CardDescription>
          Defina seu fuso horário para exibir corretamente datas e horários em todo o sistema.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <div className="space-y-2">
            <Label htmlFor="timezone">Fuso Horário</Label>
            <Select
              value={selectedTimezone}
              onValueChange={setSelectedTimezone}
              disabled={loading}
            >
              <SelectTrigger id="timezone" className="w-full">
                <SelectValue placeholder="Selecione seu fuso horário" />
              </SelectTrigger>
              <SelectContent>
                {timezoneOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <Clock className="h-4 w-4" />
            <span>
              Horário atual no fuso selecionado:{' '}
              {new Date().toLocaleTimeString('pt-BR', { timeZone: selectedTimezone })}
            </span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={isSaving || selectedTimezone === userTimezone}
          className="gap-1"
        >
          {isSaving ? (
            <>Salvando...</>
          ) : (
            <>
              <Check className="h-4 w-4" /> Salvar
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
} 